/**
 * Asset Detail Panel Component
 *
 * Displays detailed information about a selected asset.
 * Positioned on the right side of the asset grid.
 */

import { useState, useEffect } from 'react';
import type { IAsset } from '../providers';
import { useAssetNameEdit } from '../hooks';
import { AssetSource } from '../types';
import { FilesIcon, BinIcon, ExpandIcon } from '../../../icons';
import { useMode } from '../context/ModeContext';
import { ZprAlert } from '@zpr-stencil';
import type { AlertTypes } from '@zappar/zpr-ui-components-stencil';
import { AssetPreview } from './AssetPreview';
import { AssetInfoAccordion } from './AssetInfoAccordion';
import { AssetDetailActions } from './AssetDetailActions';
import { LabelsAccordion } from './LabelsAccordion';
import { useAssetActions } from '../context/AssetActionsContext';
import { InlineInput } from '../../UI/InlineInput';

export interface AssetDetailPanelProps {
  /** Array of selected assets */
  selectedAssets: IAsset[];
  /** Selected asset IDs (used for stable selection actions across filters/sorts) */
  selectedAssetIds?: string[];
  /** Total selected count including hidden assets */
  totalSelectedCount?: number;
  /** Number of selected assets currently hidden by filters */
  hiddenSelectedCount?: number;
  /** Custom CSS class */
  className?: string;
  /** Currently active source */
  activeSource: AssetSource;
  /** Callback to handle asset deletion */
  onDelete: (assetId: string, asset: IAsset) => Promise<void>;
  /** Callback to handle multiple asset deletion */
  onDeleteMultiple?: (assetIds: string[]) => Promise<void>;
  /** Callback to handle asset editing (e.g. opening in external editor or sharing) */
  onEdit?: (asset: IAsset) => void;
  /** Callback to handle asset restoration */
  onRestore?: (assetId: string) => Promise<void>;
  /** Callback to handle deselecting all assets */
  onDeselectAll?: () => void;
  /** Flag indicating if the current view is the explore view (which disables certain actions) */
  isExploreView?: boolean;
  /** Flag indicating if asset name editing is disabled */
  disableEditName?: boolean;
}

export const AssetDetailPanel = ({
  selectedAssets,
  selectedAssetIds,
  totalSelectedCount,
  hiddenSelectedCount = 0,
  className = '',
  activeSource,
  onDelete,
  onDeleteMultiple,
  onEdit,
  onRestore,
  onDeselectAll,
  isExploreView = false,
  disableEditName = false,
}: AssetDetailPanelProps) => {
  const { isToolMode, permissions } = useMode();
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const { onAddToProject, onAddToWorkspaceAssets } = useAssetActions();

  const selectedCount = totalSelectedCount ?? selectedAssets.length;
  const singleAsset = selectedCount === 1 ? selectedAssets[0] : null;

  const { editedName, isLoadingName, handleNameChange, handleSaveName, isSuccess, error } = useAssetNameEdit(singleAsset);

  useEffect(() => {
    if (!singleAsset) {
      setPreviewUrl('');
      return;
    }
    const loadUrls = async () => {
      setPreviewUrl(await singleAsset.getPreviewUrl({ width: 256 }));
    };
    loadUrls();
  }, [singleAsset]);

  const handleAddToProject = () => {
    if (!onAddToProject) return;
    if (singleAsset) {
      onAddToProject(singleAsset);
    } else {
      selectedAssets.forEach(asset => onAddToProject(asset));
    }
  };

  const handleAddToWorkspaceAssets = async () => {
    if (!onAddToWorkspaceAssets) return;
    await onAddToWorkspaceAssets(selectedAssets);
  };

  const handleMoveToBin = async () => {
    if (singleAsset && onDelete) {
      await onDelete(singleAsset.getId(), singleAsset);
    } else if (onDeleteMultiple) {
      const ids = selectedAssetIds ?? selectedAssets.map(a => a.getId());
      await onDeleteMultiple(ids);
    }
  }

  // State 1: No assets selected
  if (selectedCount === 0) {
    return (
      <div className={`asset-detail-panel asset-detail-panel--empty ${className}`}>
        <div className="asset-detail-panel__empty-state">
          <FilesIcon />
          <p>No media selected</p>
        </div>

        {activeSource === 'trash' && (
          <ZprAlert type={"warning" as AlertTypes} className='alert-plain alert-warning' dismissible={false}>
            <b>Important</b>
            Files in the bin are permanently deleted after 30 days</ZprAlert>
        )}
      </div>
    );
  }

  // State 3: Multiple assets selected
  if (selectedCount > 1) {
    return (
      <div className={`asset-detail-panel ${className}`}>
        {/* Multiple assets preview */}
        <div className="asset-detail-panel__preview asset-detail-panel__preview--multiple">
          <FilesIcon />
        </div>

        {/* List of selected assets */}
        <div className="asset-detail-panel__content">
          <div className="asset-detail-panel__header">
            <p>
              {selectedCount} Assets Selected
              {hiddenSelectedCount > 0 ? ` (${hiddenSelectedCount} hidden by filters)` : ''}
            </p>
            <button
              className="btn btn-small btn-primary"
              type="button"
              aria-label="Deselect all"
              onClick={onDeselectAll}
            >
              Deselect all
            </button>

            {isToolMode && (
              <button
                className="btn btn-small btn-primary"
                type="button"
                aria-label="Add to project"
                onClick={handleAddToProject}
              >
                Add to project
              </button>
            )}
          </div>

          {/* <div className="asset-detail-panel__section">
            <label className="asset-detail-panel__label">Selected Files</label>
            <div className="asset-detail-panel__file-list">
              {selectedAssets.map((asset) => {
                const metadata = asset.getMetadata();
                const fileSize = metadata.size || 0;

                return (
                  <details key={asset.getId()} className="asset-detail-panel__file-item">
                    <summary className="asset-detail-panel__file-summary">
                      <span className="asset-detail-panel__file-name">
                        {asset.getName()}
                      </span>
                      <span className="asset-detail-panel__file-type">
                        {asset.getMediaType()}
                      </span>
                    </summary>
                    <div className="asset-detail-panel__file-details">
                      <div className="asset-detail-panel__file-meta">
                        <span>ID: {asset.getId()}</span>
                        {fileSize > 0 && <span>Size: {formatFileSize(fileSize)}</span>}
                        {metadata.date && (
                          <span>
                            Uploaded: {formatRelativeDate(new Date(metadata.date))}
                          </span>
                        )}
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          </div> */}

          {/* Bulk actions summary */}
          {/* <div className="asset-detail-panel__section">
            <label className="asset-detail-panel__label">Total Size</label>
            <div className="asset-detail-panel__value">
              {formatFileSize(
                selectedAssets.reduce((total, asset) => {
                  const size = (asset.getMetadata().size as number) || 0;
                  return total + size;
                }, 0)
              )}
            </div>
          </div> */}
        </div>

        {/* Bulk Actions */}
        <div className="asset-detail-panel__footer">
          <div className="asset-detail-actions">
            {selectedAssets.every((asset) => asset.getMetadata().canDelete === true) && !selectedAssets.every((asset) => asset.getMetadata().binned === true) && (
              <div>
                <button
                  className="btn btn-ghost p0 w100"
                  type="button"
                  onClick={handleMoveToBin}
                  aria-label="Move to bin"
                >
                  <BinIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                  Move to bin
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  // Edge case: one selected ID but asset object not currently available in cache
  if (selectedCount === 1 && !singleAsset) {
    return (
      <div className={`asset-detail-panel asset-detail-panel--empty ${className}`}>
        <div className="asset-detail-panel__empty-state">
          <FilesIcon />
          <p>1 Asset Selected</p>
          {hiddenSelectedCount > 0 && <p>({hiddenSelectedCount} hidden by filters)</p>}
        </div>
      </div>
    );
  }

  // State 2: Single asset selected
  const asset = singleAsset!;
  const metadata = asset.getMetadata();
  const assetName = asset.getName();
  const assetType = asset.getMediaType();
  const assetId = asset.getId();

  // Extract metadata fields
  const status = metadata.status || 'ready';
  // const isPublic = (metadata.access === 'public') || false;
  const canDelete = (metadata.canDelete as boolean) || false;
  const binned = (metadata.binned as boolean) || false;
  const canRestore = (metadata.canRestore as boolean) || false;

  return (
    <div className={`asset-detail-panel ${className}`}>

      {/* Preview */}
      <div className="asset-detail-panel__preview">
        <AssetPreview
          previewUrl={previewUrl}
          mediaType={assetType}
          name={assetName}
        />

        {status !== 'ready' && (
          <div className="asset-detail-panel__status-badge">
            {status === 'pending' ? 'Processing...' : 'Error'}
          </div>
        )}

        { isToolMode && onEdit && (
          <button
            className="asset-detail-panel__preview-button"
            type="button"
            aria-label="Asset details"
            onClick={() => onEdit(asset)}
          >
            <ExpandIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
          </button>
        )}
      </div>

      {/* Asset Information */}
      <div className="asset-detail-panel__content">
        <div className="asset-detail-panel__header">
          <InlineInput
            name="asset-name"
            readOnly={binned || disableEditName}
            disabled={binned || isLoadingName}
            value={editedName}
            onChange={handleNameChange}
            onEdit={handleSaveName}
            placeholder="Enter asset name"
            ariaLabel="Asset name"
            isLoading={isLoadingName}
            isSuccess={isSuccess}
            isError={!!error}
            errorMessage={error || 'Failed to update asset name'}
            className="asset-name-input-field"
          />

          { !binned && (
            <>
              {!isToolMode && onEdit && (
                <button
                  className="btn btn-small btn-primary"
                  type="button"
                  aria-label="View asset"
                  onClick={() => onEdit(asset)}
                >
                  View asset
                </button>
              )}

              {isToolMode && (
                <button
                  className="btn btn-small btn-primary"
                  type="button"
                  aria-label="Add to project"
                  onClick={handleAddToProject}
                >
                  Add to project
                </button>
              )}
              {/* {isExploreView && activeSource !== 'workspace-library' && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="added-to-workspace"
                    checked={isPublic}
                    onChange={(e) => { if (e.target.checked) { handleAddToWorkspaceAssets() }}}
                  />
                  <span>Add to workspace assets</span>
                </label>
              )} */}
            </>
          )}
        </div>

        {/* Information Accordion */}
        <AssetInfoAccordion
          asset={asset}
        />
        {activeSource !== 'user-library' && activeSource !== 'explore' && (
          <LabelsAccordion
            key={asset.getId()}
            asset={asset}
            isEditableAsset={!binned}
            canEditLabels={permissions?.canEditLabels === true}
            canAssignLabels={permissions?.canAssignLabels === true}
          />
        )}
      </div>
      {/* Actions */}
      <div className="asset-detail-panel__footer">
        <AssetDetailActions
          binned={binned}
          canDelete={canDelete}
          canRestore={canRestore}
          activeSource={activeSource}
          onMoveToBin={handleMoveToBin}
          onAddToWorkspaceAssets={() => handleAddToWorkspaceAssets()}
          onEdit={isToolMode && onEdit ? () => onEdit(asset) : undefined}
          onRestore={onRestore ? () => onRestore(assetId) : undefined}
          havePermissionToDelete={permissions?.canDeleteAsset === true}
        />
      </div>
    </div>
  );
};
